import React, { useState, forwardRef, ForwardRefRenderFunction } from "react";
//#region redux
import { closeDialog } from "store/actions/utilActions";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { invoiceCredited } from "store/actions/invoiceActions";
//#endregion
//#region types
import { InvoiceEnums, StyleEnums } from "models/enums";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { RouteConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CreditInvoiceSelectionFormProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { useNavigate, useLocation } from "react-router";
import { SelectionTiles, useFloatingMessages, StaticInfoMessage } from "components/common";
import useInvoicePreview from "../usePreview";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, EnumHelper, UrlHelper, DateHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const CreditInvoiceSelectionForm: ForwardRefRenderFunction<FormHandles, CreditInvoiceSelectionFormProps> = (
  props,
  ref
) => {
  const invoiceService = useInvoiceService();
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const { enqueueMessage } = useFloatingMessages();
  const { showPreviewDialog } = useInvoicePreview();

  const { invoiceId, isTimeReportingInvoice } = props;

  React.useImperativeHandle(ref, () => ({
    submit: async () => {
      if (selectedItem.id === InvoiceEnums.CreditInvoiceSendType.Edit) {
        dispatch(closeDialog());
        navigate(
          UrlHelper.formatPath(
            `/Invoice/Create?${RouteConstants.QueryParamStrings.InvoiceTemplate}=${props.invoiceId}&${RouteConstants.QueryParamStrings.IsCredit}=true`
          ),
          {
            state: { background: location },
          }
        );
      } else {
        const creditInvoice = await invoiceService.getCreditInvoice(invoiceId);
        const { ocr, ...otherFields } = creditInvoice;

        const creditInvoicePut: WintSuperkollAPIModel.InvoicePut = {
          ...otherFields,
          rows: creditInvoice.rows?.map((row) => {
            const periodisationStart = row.periodisationStartMonth
              ? DateHelper.splitYearMonth(row.periodisationStartMonth)
              : undefined;
            const periodisationEnd = row.periodisationEndMonth
              ? DateHelper.splitYearMonth(row.periodisationEndMonth)
              : undefined;

            return {
              ...row,
              overriddenUnitPrice: row.unitPrice ?? 0,
              overriddenVat: row.vat ?? 0,
              periodisationStartMonth: periodisationStart && periodisationStart.month,
              periodisationStartYear: periodisationStart && periodisationStart.year,
              periodisationEndMonth: periodisationEnd && periodisationEnd.month,
              periodisationEndYear: periodisationEnd && periodisationEnd.year,
            };
          }),
          useStrictValidation: true,
          attachForwardedCostDocuments: false,
        };

        const reloadInvoice = () => {
          enqueueMessage(
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CreditInvoiceCreateSuccessMessage),
            StyleEnums.DisplayType.Success
          );
          dispatch(invoiceCredited(invoiceId));

          if (selectedItem.id === InvoiceEnums.CreditInvoiceSendType.CreditFullSendNew)
            navigate(
              UrlHelper.formatPath(
                `/Invoice/Create?${RouteConstants.QueryParamStrings.InvoiceTemplate}=${props.invoiceId}`
              ),
              {
                state: { background: location },
              }
            );
          else if (props.reloadInvoice) {
            props.reloadInvoice();
          }
        };

        dispatch(closeDialog());
        showPreviewDialog(
          creditInvoice,
          creditInvoicePut,
          undefined,
          reloadInvoice,
          undefined,
          undefined,
          undefined,
          creditInvoice.totalAmount + (creditInvoice.totalReminderCost ?? 0)
        );
      }
    },
  }));

  const onChange = (selectedItems: SelectionItem[]) => {
    setSelectedItem(selectedItems[0]);
  };

  const creditTypes = React.useMemo(() => {
    return EnumHelper.getNumericItemValues(InvoiceEnums.CreditInvoiceSendType).map(
      (key): SelectionItem => ({
        id: Number(key),
        value: LocalizationHelperInstance.getFormattedMessage(`Invoice.CreditInvoiceSendType_${key}`),
        description: LocalizationHelperInstance.getFormattedMessage(`Invoice.CreditInvoiceSendTypeDescription_${key}`),
        selectionTileProps: { gridColumnCount: 6 },
      })
    );
  }, []);

  const getInitialSelectedItem = (creditTypes: SelectionItem[]) => {
    if (isTimeReportingInvoice) {
      return creditTypes[InvoiceEnums.CreditInvoiceSendType.CreditFull];
    }
    return creditTypes[InvoiceEnums.CreditInvoiceSendType.CreditFullSendNew];
  };
  const [selectedItem, setSelectedItem] = useState<SelectionItem>(getInitialSelectedItem(creditTypes));

  React.useEffect(() => {
    props.onFormValidationChange && props.onFormValidationChange(selectedItem !== undefined);
  }, [selectedItem, props.onFormValidationChange]);

  return (
    <>
      <SelectionTiles
        items={creditTypes}
        value={[selectedItem]}
        onChange={onChange}
        hideViewAll
        className={cssExports.creditInvoiceSelection}
      />
      <br></br>
      {isTimeReportingInvoice && (
        <StaticInfoMessage>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CreditInfoWhenTimeReportInvoice)}
        </StaticInfoMessage>
      )}
    </>
  );
};

export default forwardRef(CreditInvoiceSelectionForm);
